import { useMemo, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { toLocalDateTime } from "./Data/Date";
import { Button } from "./Controls/Button";
import { Button as BsButton } from "react-bootstrap";
import { formatAuditValue, isAddRemoveEvent } from "./helpers/AuditLogsHelper";

export function SubscriptionAuditLogModal({ show, onHide, auditEntries }) {
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  const filtered = useMemo(() => {
    let list = [...auditEntries];

    if (from) {
      list = list.filter((x) => new Date(x.timestamp) >= new Date(from));
    }

    if (to) {
      const end = new Date(to + "T23:59:59.999Z");
      list = list.filter((x) => new Date(x.timestamp) <= end);
    }

    return list.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [auditEntries, from, to]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Audit Log</Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}
      >
        <div className="d-flex flex-md-nowrap flex-wrap gap-2 mb-3">
          <Form.Control
            className="flex-grow-1"
            type="date"
            value={from}
            onChange={(e) => {
              const newFrom = e.target.value;
              setFrom(newFrom);
              if (to && to < newFrom) setTo(newFrom);
            }}
          />

          <Form.Control
            className="flex-grow-1"
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
          />

          <BsButton
            variant="outline-secondary"
            className="rounded-4 w-100 w-md-auto"
            onClick={() => {
              setFrom("");
              setTo("");
            }}
          >
            Clear
          </BsButton>
        </div>

        {filtered.length === 0 && (
          <div className="text-muted">No audit entries found.</div>
        )}

        {filtered.map((entry, i) => (
          <AuditRecordCard key={i} entry={entry} />
        ))}
      </Modal.Body>

      <Modal.Footer>
        <Button className="w-100" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function AuditRecordCard({ entry }) {
  const isAddRemove = isAddRemoveEvent(entry.eventName);

  const isParkerSpotChange =
    entry.eventName === "SubscriptionParkerSpotQuantityChangedEvent";

  const isIntegrationEnroll = entry.eventName === "EnrollIntegrationEvent";
  const isIntegrationUnroll = entry.eventName === "UnrollIntegrationEvent";

  const isParkGuardSuccess =
    entry.eventName === "ParkGuardSubscriptionSuccessEvent";
  const isParkGuardFail =
    entry.eventName === "ParkGuardSubscriptionFailedEvent";

  let integrationName = "";

  if (isParkGuardSuccess || isParkGuardFail) {
    return (
      <div className="p-3 mb-3 rounded border border-secondary">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3 gap-1">
        <div
          className="fw-bold"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {toLocalDateTime(entry.timestamp)} • {entry.eventName}
        </div>
        <div
          className="text-muted small d-md-none"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {entry.changedBy}
        </div>
        <div className="text-muted small d-none d-md-block">
          {entry.changedBy}
        </div>
      </div>

        {isParkGuardSuccess && (
          <div className="fw-semibold">✓ Park Guard added</div>
        )}

        {isParkGuardFail && (
          <div className="fw-semibold">✕ Park Guard failed</div>
        )}
      </div>
    );
  }

  if (isIntegrationEnroll || isIntegrationUnroll) {
    const raw =
      entry.newValues?.IntegrationType || entry.newValues?.IntegrationName;
    integrationName = raw ?? "Integration";
  }

  return (
    <div className="p-3 mb-3 rounded border border-secondary">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3 gap-1">
        <div
          className="fw-bold"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {toLocalDateTime(entry.timestamp)} • {entry.eventName}
        </div>
        <div
          className="text-muted small d-md-none"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {entry.changedBy}
        </div>
        <div className="text-muted small d-none d-md-block">
          {entry.changedBy}
        </div>
      </div>

      {shouldShowParkerCard(
        entry,
        isParkerSpotChange,
        isIntegrationEnroll,
        isIntegrationUnroll
      ) && (
        <div className="mb-3">
          <div className="fw-semibold mb-1">Parker</div>
          <div
            className={
              "fw-semibold p-2 rounded " +
              (isIntegrationEnroll
                ? "bg-success bg-opacity-25 text-success"
                : "bg-danger bg-opacity-25 text-danger")
            }
          >
            {entry.newValues.ParkerName}
          </div>
        </div>
      )}

      {(isIntegrationEnroll || isIntegrationUnroll) && (
        <div className="fw-semibold">
          {isIntegrationEnroll ? "✓ Added" : "✕ Removed"} {integrationName}
        </div>
      )}

      {isAddRemove && (
        <div className="fw-semibold">
          {entry.eventName.includes("Created")
            ? `✓ ${entry.entityName} was added`
            : `✕ ${entry.entityName} was removed`}
        </div>
      )}

      {shouldShowChanges({
        isAddRemove,
        isIntegrationEnroll,
        isIntegrationUnroll,
      }) &&
        entry.changes.map((c) => (
          <div key={c.field} className="mb-4">
            <div className="fw-semibold mb-2">{c.field}</div>

            <div className="row g-2">
              <div className="col-6">
                <div className="small text-muted mb-1">Previous</div>
                <div className="p-2 rounded bg-danger bg-opacity-25 text-danger text-decoration-line-through">
                  {formatAuditValue(c.oldValue, c.field)}
                </div>
              </div>

              <div className="col-6">
                <div className="small text-muted mb-1">Updated</div>
                <div className="p-2 rounded bg-success bg-opacity-25 text-success">
                  {formatAuditValue(c.newValue, c.field)}
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

function shouldShowParkerCard(
  entry,
  isParkerSpotChange,
  isIntegrationEnroll,
  isIntegrationUnroll
) {
  return (
    (isParkerSpotChange || isIntegrationEnroll || isIntegrationUnroll) &&
    entry?.newValues?.ParkerName
  );
}

const shouldShowChanges = ({
  isAddRemove,
  isIntegrationEnroll,
  isIntegrationUnroll,
}) => {
  return !isAddRemove && !isIntegrationEnroll && !isIntegrationUnroll;
};
